import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import type { CreateOrUpdateRewardRequest, RewardResponse } from "../../rewards/rewardsApi";
import { deleteReward, updateReward } from "../../rewards/rewardsApi";
import { useRewardWithId } from "../../rewards/useRewardWithId";

export const useReward = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { rewardId } = useParams<{ rewardId: string }>();
  const { data, isLoading } = useRewardWithId(rewardId);

  const updateRewardMutation = useMutation<RewardResponse, unknown, CreateOrUpdateRewardRequest>({
    mutationFn: (v) => updateReward(rewardId!, v),
    onSuccess: async (data) => {
      enqueueSnackbar(`Nagroda ${data.name} została zaktualizowana`, { variant: "success" });
      await queryClient.setQueryData(["rewards", rewardId], data);
      await queryClient.invalidateQueries({ queryKey: ["rewards"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować nagrody. Powód: ${(error as Error).message}`, { variant: "error" });
    },
  });

  const deleteRewardMutation = useMutation<void, unknown, { rewardId: string }>({
    mutationFn: ({ rewardId }) => deleteReward(rewardId),
    onSuccess: async () => {
      enqueueSnackbar("Nagroda została usunięta", { variant: "success" });
      queryClient.removeQueries({ queryKey: ["rewards", rewardId] });
      await queryClient.invalidateQueries({ queryKey: ["rewards"] });
    },
  });

  return {
    reward: data,
    isLoading,
    deleteReward: (rewardId: string) => deleteRewardMutation.mutateAsync({ rewardId }),
    updateReward: (update: CreateOrUpdateRewardRequest) => updateRewardMutation.mutateAsync(update),
  };
};
